import React, {useEffect, useMemo, useState} from 'react';
import {useHotkeys} from 'react-hotkeys-hook';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {useMatch} from 'react-router-dom';
import {toast} from 'react-toastify';
import {ActionCreators} from 'redux-undo';
import {useDebouncedCallback} from 'use-debounce';
import {SlideElementHotkeys} from '../../../constants/slideElementHotkeys';
import MediaErrorContextProvider from '../../../context/MediaErrorContext';
import SlideRefContextProvider from '../../../context/SlideRefContext';
import {PresScaleProvider} from '../../../context/PresScaleContext';
import {useElementCleanup} from '../../../hooks/elementCleanup/useElementCleanup';
import {usePresentationThemeDetailQuery} from '../../../hooks/usePresentationThemeDetailQuery';
import {setSidePanelContentAction} from '../../../store/actions/grid';
import {startWindowResizeSpinnerAction, stopWindowResizeSpinnerAction} from '../../../store/actions/spinner';
import {SidePanelContent} from '../../../store/actions/types/grid.types';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {
    deletedEmittedSharedPresentationSelector,
    emittedSharedPresentationSelector,
    selectedPresentationSelector,
} from '../../../store/selectors/presentation.selectors';
import {emittedSlideSelector, isSlideModifiedSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {addFontFamilyToHead, defaultFonts} from '../../../utils/defaultFonts';
import useSaveSlide from '../../../utils/hooks/slideUpdates/useSaveSlide';
import {areSlidesDifferent} from '../../../utils/slide';
import CustomToastContainer from '../../components/common/CustomToastContainer';
import Toolbar from '../../components/common/navbar/toolbar/Toolbar';
import {ElementContextMenuPortal} from '../../components/contextMenu/ElementContextMenuPortal';
import SlideContextMenuTrigger from '../../components/contextMenu/SlideContextMenu';
import {SlideContextMenuPortal} from '../../components/contextMenu/SlideContextMenuPortal';
import {SlidePreviewContextMenuPortal} from '../../components/contextMenu/SlidePreviewContextMenuPortal';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/scrollbar';
import SlidePreviewFooter from '../../components/slidePreviewFooter/SlidePreviewFooter';
import {useSlideUpdateContext} from '../../context/SetSlideUpdateContext';
import useSelectPresToEdit from '../../util/useSelectPresToEdit';
import DeleteSlideModal from './components/DeleteSlideModal';
import Modals from './components/Modals';
import PresentationSettingsModal from './components/settings/PresentationSettingsModal';
import SidePanelWrapper from './components/SidePanelWrapper';
import FloatingElements from './components/slideContainer/FloatingElements';
import SlidesContainerWrapper from './components/slideContainer/SlidesContainerWrapper';
import {windowResizeSpinnerSelector} from '../../../store/selectors/spinner.selector';
import {AppState} from '../../../store/reducer';
import {flushPendingRichTextEdits} from '../../../components/elements/tipTap/richTextPart/pendingEdits';

export const HIGHLIGHTED_ELEMENT_INITIAL = -1;

function PresentationEditPage(): JSX.Element {
    const dispatch = useDispatch();

    const match = useMatch('presentations/:presentationId');
    const {t} = useTranslation();

    useSelectPresToEdit(match?.params.presentationId);
    useElementCleanup();

    const selectedPresentation = useSelector(selectedPresentationSelector);
    const slide = useSelector(selectedSlideSelector);
    const modified = useSelector(isSlideModifiedSelector);
    const {data: theme} = usePresentationThemeDetailQuery(selectedPresentation.presentationThemeId);
    const emittedSlide = useSelector(emittedSlideSelector);
    const emittedSharedPresentation = useSelector(emittedSharedPresentationSelector);
    const deletedEmittedSharedPresentationIds = useSelector(deletedEmittedSharedPresentationSelector);
    const loggedInUser = useSelector(loggedInUserSelector);
    const windowResizeSpinner = useSelector(windowResizeSpinnerSelector);

    const [deleteModal, setDeleteModal] = useState(false);
    const [emittedSlideModal, setEmittedSlideModal] = useState(false);
    const [emittedSharedPresentationModal, setEmittedSharedPresentationModal] = useState(false);

    const {mutate: mutateSlide} = useSaveSlide({setEmittedSlideModal});
    const {setAndSaveSlide} = useSlideUpdateContext();
    const store = useStore<AppState>();

    const allFonts = useMemo(
        () => (theme?.fonts || []).concat(theme?.defaultFonts ? theme.defaultFonts : defaultFonts),
        [theme?.fonts, theme?.defaultFonts]
    );

    useEffect(() => {
        return () => {
            dispatch(ActionCreators.clearHistory());
        };
    }, [dispatch]);

    useEffect(() => {
        if (emittedSlide._id !== '' && emittedSlide._id === slide._id && areSlidesDifferent(slide, emittedSlide)) {
            const toastId = 'emittedSlideToast';
            if (!toast.isActive(toastId)) {
                toast.info(
                    <CustomToastContainer
                        message={t('editorPage.sharePresentations.sharePresentationModal.toastMessage.slideChanged')}
                        buttonAction={() => setEmittedSlideModal(true)}
                    />,
                    {
                        autoClose: 10000,
                        toastId,
                    }
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emittedSlide, t]);

    useEffect(() => {
        if (
            (emittedSharedPresentation.presentationId === selectedPresentation._id &&
                emittedSharedPresentation.isReadOnly &&
                emittedSharedPresentation.userId === loggedInUser?._id) ||
            deletedEmittedSharedPresentationIds.includes(selectedPresentation._id)
        ) {
            setEmittedSharedPresentationModal(true);
        }
    }, [deletedEmittedSharedPresentationIds, emittedSharedPresentation, loggedInUser?._id, selectedPresentation._id]);

    useEffect(() => {
        const cleanupFunctions: Array<() => void> = [];

        if (theme && (theme.fonts || theme.defaultFonts)) {
            allFonts.forEach((font) => {
                const cleanup = addFontFamilyToHead(font);
                cleanupFunctions.push(cleanup);
            });
        } else {
            defaultFonts.forEach((font) => {
                const cleanup = addFontFamilyToHead(font);
                cleanupFunctions.push(cleanup);
            });
        }

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, [allFonts, theme]);

    useEffect(() => {
        const callback = (e: BeforeUnloadEvent): void => {
            e.preventDefault();
            e.returnValue = t('presEditPage.changesMayNotBeSaved');
        };

        if (modified) {
            window.addEventListener('beforeunload', callback);
        }

        return () => {
            window.removeEventListener('beforeunload', callback);
        };
    }, [selectedPresentation, modified, t]);

    const handleResizeStop = (): void => {
        dispatch(stopWindowResizeSpinnerAction());
    };

    const debouncedResizeStop = useDebouncedCallback(handleResizeStop, 300);

    useEffect(() => {
        const handleResize = (): void => {
            dispatch(startWindowResizeSpinnerAction());
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('resize', debouncedResizeStop);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('resize', debouncedResizeStop);
        };
    }, [debouncedResizeStop, dispatch]);

    useHotkeys(
        SlideElementHotkeys.Save.keys,
        (keyboardEvent) => {
            keyboardEvent.preventDefault();
            flushPendingRichTextEdits();
            const state = store.getState();
            if (isSlideModifiedSelector(state)) {
                setAndSaveSlide(selectedSlideSelector(state));
            }
        },
        {preventDefault: true, enableOnContentEditable: true}
    );

    if (selectedPresentation?._id !== match?.params.presentationId || !slide) return <div />;

    return (
        <SlideRefContextProvider>
            <PresScaleProvider>
                <SlideContextMenuPortal />
                <SlidePreviewContextMenuPortal />
                <ElementContextMenuPortal />
                <div className="p-presEdit__body">
                    <SlidePreviewFooter />
                    <div className="p-presEdit__content">
                        <MediaErrorContextProvider>
                            <SlideContextMenuTrigger>
                                <div className="p-presEdit__slideContainerWrapper">
                                    {!windowResizeSpinner && (
                                        <div
                                            className="p-presEdit__slideContainerWrapper_main"
                                            onClick={(e) => {
                                                if (e.target !== e.currentTarget) return;
                                                dispatch(setSidePanelContentAction(SidePanelContent.SLIDE));
                                            }}
                                        >
                                            <Toolbar />
                                            <SlidesContainerWrapper />
                                            <FloatingElements />
                                        </div>
                                    )}
                                </div>
                            </SlideContextMenuTrigger>
                        </MediaErrorContextProvider>
                    </div>

                    <SidePanelWrapper />
                </div>

                <DeleteSlideModal deleteModalOpen={deleteModal} setDeleteModalOpen={setDeleteModal} slides={[slide]} />

                <Modals
                    emittedSharedPresentationModal={emittedSharedPresentationModal}
                    mutateSlide={mutateSlide}
                    setEmittedSlideModal={setEmittedSlideModal}
                    emittedSlideModal={emittedSlideModal}
                    setEmittedSharedPresentationModal={setEmittedSharedPresentationModal}
                />

                <PresentationSettingsModal presentation={selectedPresentation} />
            </PresScaleProvider>
        </SlideRefContextProvider>
    );
}

export default PresentationEditPage;
